import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Turnstile } from '../components/Turnstile'
import { supabase } from '../lib/supabase'
import { captchaEnabled } from '../lib/turnstile'

export const Route = createFileRoute('/login')({
  // Where to send the user after a successful login, if they were bounced here
  // from a protected page.
  // The return type marks `redirect` OPTIONAL, which is what lets a plain
  // <Link to="/login"> compile without supplying search params.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LoginPage,
})

/**
 * Supabase's own error text is English and lives in their SDK, so it can never
 * be translated in place. Switch on the code and always fall back to something
 * Spanish rather than rendering `error.message`, which produces things like
 * "For security purposes, you can only request this after 45 seconds."
 */
function describeAuthError(error: { code?: string; message: string }): string {
  switch (error.code) {
    case 'over_email_send_rate_limit':
    case 'over_request_rate_limit':
      return 'Se enviaron demasiados pedidos. Hay que esperar un minuto e intentar otra vez.'
    case 'validation_failed':
      return 'La dirección de correo no parece válida.'
    case 'captcha_failed':
      // Turnstile's token was refused or had already been used. The widget is
      // reset after every attempt, so a second try usually just works.
      return 'No se pudo comprobar que el pedido lo hace una persona. Intentar de nuevo.'
    default:
      return 'No se pudo enviar el enlace. Intentar de nuevo en un momento.'
  }
}

function LoginPage() {
  const { redirect } = Route.useSearch()
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  // Only used when Turnstile is on (lib/turnstile.ts). A token is single-use,
  // so every attempt bumps resetKey and the widget fetches a fresh one.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaResetKey, setCaptchaResetKey] = useState(0)
  const [captchaUnavailable, setCaptchaUnavailable] = useState(false)
  const waitingForCaptcha = captchaEnabled && !captchaToken

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setStatus('sending')

    // Carry the intended destination THROUGH the email. Without this the
    // `redirect` search param dies here, and somebody who was three taps into
    // choosing a slot lands on /me instead of finishing their booking.
    // searchParams.set percent-encodes the ? and & inside the value.
    const callback = new URL('/auth/callback', window.location.origin)
    if (redirect) callback.searchParams.set('redirect', redirect)

    // Magic link / OTP: no passwords to manage, per the spec.
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: callback.toString(),
        // Ignored by Supabase until Turnstile is switched on there.
        ...(captchaEnabled && captchaToken ? { captchaToken } : {}),
      },
    })
    if (captchaEnabled) setCaptchaResetKey((key) => key + 1)

    if (error) {
      setErrorMessage(describeAuthError(error))
      setStatus('error')
      return
    }
    setStatus('sent')
  }

  if (status === 'sent') {
    return (
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold">Enlace enviado</h1>
        <p className="text-slate-600">
          Se envió un enlace de acceso a <span className="font-medium">{email}</span>. Hay que
          abrirlo en este mismo dispositivo.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-sm space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Iniciar sesión</h1>
        <p className="mt-1 text-slate-600">Llega un enlace por correo. No hace falta contraseña.</p>
      </div>

      <input
        type="email"
        required
        value={email}
        onChange={(event) => setEmail(event.target.value)}
        placeholder="tu@correo.com"
        className="min-h-12 w-full rounded-md border border-slate-300 px-3 focus:border-slate-900 focus:outline-none"
      />

      {captchaEnabled && (
        <Turnstile
          onToken={setCaptchaToken}
          onUnavailable={() => setCaptchaUnavailable(true)}
          resetKey={captchaResetKey}
        />
      )}

      <button
        type="submit"
        disabled={status === 'sending' || waitingForCaptcha}
        className="min-h-12 w-full rounded-md bg-slate-900 px-4 text-white hover:bg-slate-700 disabled:opacity-50"
      >
        {status === 'sending' ? 'Enviando…' : 'Enviarme un enlace'}
      </button>

      {status === 'error' && <p className="text-sm text-red-600">{errorMessage}</p>}
      {captchaUnavailable && !captchaToken && (
        <p className="text-sm text-red-600">
          No se pudo completar la comprobación de seguridad. Recargar la página.
        </p>
      )}
    </form>
  )
}
